import { GET, POST, PATCH, deleteRequest } from '../configs/api';
import { api } from './constants';

/**
 * @returns {Promise<Array<{ id: string; name: string; color?: string | null; is_active?: boolean }>>}
 */
export async function fetchJobTags({ activeOnly = true } = {}) {
  const query = activeOnly ? '?active=true' : '';
  const { data, error } = await GET(`${api.getJobTags}${query}`);
  if (error) return [];
  return Array.isArray(data?.tags) ? data.tags : [];
}

/**
 * @returns {Promise<{ id: string; name: string; color?: string | null } | null>}
 */
export async function addJobTag(name, color = null) {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  if (!trimmed) return null;

  const payload = { name: trimmed };
  if (color) payload.color = color;

  const { data, error } = await POST(api.createJobTag, {}, payload);
  if (error) return null;
  return data ?? null;
}

/**
 * @returns {Promise<{ id: string; name: string; color?: string | null; is_active?: boolean } | null>}
 */
export async function updateJobTag(id, { name, color, is_active: isActive } = {}) {
  if (!id) return null;

  const payload = {};
  if (name !== undefined) {
    const trimmed = typeof name === 'string' ? name.trim() : '';
    if (!trimmed) return null;
    payload.name = trimmed;
  }
  if (color !== undefined) payload.color = color || null;
  if (isActive !== undefined) payload.is_active = isActive;

  if (Object.keys(payload).length === 0) return null;

  const { data, error } = await PATCH(`${api.updateJobTag}/${id}`, {}, payload);
  if (error) return null;
  return data ?? null;
}

/**
 * @returns {Promise<boolean>}
 */
export async function removeJobTag(id) {
  if (!id) return false;
  const { error } = await deleteRequest({ url: `${api.deleteJobTag}/${id}` });
  return !error;
}

export function sortTagOptions(options) {
  return [...options].sort((a, b) => a.name.localeCompare(b.name));
}

export function upsertTagOption(options, entry) {
  if (!entry?.id || !entry?.name) return options;
  if (options.some((option) => option.id === entry.id)) return options;
  return sortTagOptions([...options, entry]);
}

/**
 * Resolve Autocomplete values, creating catalog tags when needed.
 * @returns {Promise<Array<{ id: string; name: string; color?: string | null }>>}
 */
export async function resolveTagSelection(newValue, canCreate) {
  const resolved = [];

  for (const item of newValue) {
    if (item?.inputValue) {
      if (!canCreate) continue;
      const created = await addJobTag(item.inputValue);
      if (created) {
        resolved.push(created);
      }
      continue;
    }

    if (item?.id && item?.name) {
      resolved.push(item);
    }
  }

  return resolved;
}

export function getTagChipSx(tag, theme) {
  if (!tag?.color) return {};
  return {
    bgcolor: tag.color,
    color: theme.palette.getContrastText(tag.color),
    borderColor: tag.color,
  };
}
